import type { Ref } from "vue";
import { HttpClient } from "@/services/httpClient";
import { GetAccountingSettings } from "@/api/accountingSettingsApi";
import {
  CreateSalesInvoicePaymentEntry,
  GetPaymentEntries,
  GetPaymentEntryDetail,
} from "@/api/paymentEntryApi";
import {
  fetchFrappeDocCount,
  normalizeFrappeListResult,
  withFrappeLoading,
} from "@/services/frappe/list";
import { serializeFrappeFilters, type FrappeFilterClause } from "@/services/frappe/filters";
import { Controllers, FrappeMethodType } from "@/type/api";
import type {
  SalesInvoiceAmendResult,
  SalesInvoiceCancelContext,
  SalesInvoiceDeleteResult,
  SalesInvoiceDetail,
  SalesInvoicePaymentHistoryItem,
  SalesInvoiceAppendItemPayload,
  SalesInvoiceAppendItemResult,
  SalesInvoiceDraftItem,
  SalesInvoiceFilterParam,
  SalesInvoiceEnsureDraftResult,
  SalesInvoiceListItem,
  SalesInvoiceListResult,
  SalesInvoicePayload,
  SalesInvoiceReturnReplacePayload,
  SalesInvoiceReturnReplaceResult,
  SalesInvoiceStatus,
} from "@/type/salesInvoice";
import { DEFAULT_SELLING_PRICE_LIST } from "@/config/pricing";
import { applyBranchToDoc } from "@/helper/branchGuard";

const SALES_INVOICE_DOCTYPE = "Sales Invoice";
export const DEFAULT_SALES_INVOICE_CURRENCY = "IQD";
const salesInvoiceService = new HttpClient(Controllers.salesInvoice);
let cachedDefaultCompany: string | null | undefined;

const SALES_INVOICE_BASE_FIELDS = [
  "name",
  "customer",
  "customer_name",
  "company",
  "posting_date",
  "due_date",
  "po_no",
  "set_warehouse",
  "remarks",
  "net_total",
  "total",
  "total_taxes_and_charges",
  "currency",
  "selling_price_list",
  "apply_discount_on",
  "discount_amount",
  "additional_discount_percentage",
  "grand_total",
  "outstanding_amount",
  "status",
  "update_stock",
  "is_return",
  "is_pos",
  "return_against",
  "amended_from",
  "docstatus",
  "modified",
  // The Branch stamped at insert (`applyBranchToDoc`). Read so the POS can list the open
  // drafts of ITS branch — clinic and counter alike — without a customer first.
  "branch",
  // Who made the invoice and when, for the invoice list. Standard columns on every doctype,
  // so unlike a custom field they cannot take the list query down.
  "owner",
  "creation",
];

/**
 * `total_advance` is requested optimistically and dropped for good on the first refusal.
 *
 * ⚠️ A LIST QUERY THAT NAMES AN UNKNOWN FIELD FAILS ENTIRELY — not row by row, the whole
 * result. The blast radius here is the POS open-invoice bar and the branch drafts card
 * coming back empty, i.e. a cashier who cannot find the customer's bill at all. That is a
 * far worse outcome than showing the gross, so the field is never allowed to take the query
 * down with it: one retry without it, then the latch keeps every later call cheap.
 *
 * It is core ERPNext (v11+), so this should never fire. It exists because the failure it
 * guards is silent and total.
 */
let advanceFieldUnavailable = false;

function salesInvoiceFields() {
  return JSON.stringify(
    advanceFieldUnavailable
      ? SALES_INVOICE_BASE_FIELDS
      : [...SALES_INVOICE_BASE_FIELDS, "total_advance"],
  );
}

/**
 * Only a refusal that actually names the field counts. Latching on any error would drop the
 * advance figure permanently the first time the network blipped, and a POS that quietly
 * reverts to collecting the gross is the exact bug this all exists to fix.
 */
function namesAdvanceField(error: unknown) {
  const detail = error as { message?: unknown; response?: { data?: unknown } } | null;

  return /total_advance/i.test(
    `${String(detail?.message ?? "")} ${JSON.stringify(detail?.response?.data ?? "")}`,
  );
}

interface SalesInvoiceRow {
  name?: string | null;
  customer?: string | null;
  customer_name?: string | null;
  company?: string | null;
  posting_date?: string | null;
  /** Present on a full doc read only; never requested in the list field set. */
  posting_time?: string | null;
  due_date?: string | null;
  po_no?: string | null;
  set_warehouse?: string | null;
  remarks?: string | null;
  net_total?: number | string | null;
  total?: number | string | null;
  total_taxes_and_charges?: number | string | null;
  currency?: string | null;
  selling_price_list?: string | null;
  apply_discount_on?: string | null;
  discount_amount?: number | string | null;
  additional_discount_percentage?: number | string | null;
  grand_total?: number | string | null;
  outstanding_amount?: number | string | null;
  total_advance?: number | string | null;
  status?: string | null;
  update_stock?: number | string | boolean | null;
  is_return?: number | string | boolean | null;
  is_pos?: number | string | boolean | null;
  return_against?: string | null;
  amended_from?: string | null;
  docstatus?: number | string | null;
  modified?: string | null;
  branch?: string | null;
  owner?: string | null;
  creation?: string | null;
  items?: SalesInvoiceItemRow[] | null;
}

interface SalesInvoiceItemRow {
  name?: string | null;
  item_code?: string | null;
  item_name?: string | null;
  description?: string | null;
  uom?: string | null;
  stock_uom?: string | null;
  conversion_factor?: number | string | null;
  warehouse?: string | null;
  qty?: number | string | null;
  rate?: number | string | null;
  amount?: number | string | null;
  price_list_rate?: number | string | null;
  discount_percentage?: number | string | null;
  discount_amount?: number | string | null;
}

interface SalesInvoiceCurrencySource {
  sellingPriceList?: unknown;
  priceListCurrency?: unknown;
  currency?: unknown;
  selling_price_list?: unknown;
  price_list_currency?: unknown;
  conversion_rate?: unknown;
  plc_conversion_rate?: unknown;
}

/**
 * Money rounding shared by the payload builder and the items table, so the total on
 * screen and the total in the payload can never disagree by a floating-point tail.
 */
export function roundInvoiceAmount(value: unknown) {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric))
    return 0;

  return Math.round((numeric + Number.EPSILON) * 100) / 100;
}

function normalizeText(value: unknown) {
  const normalized = String(value ?? "").trim();
  return normalized || null;
}

async function getDefaultCompanyFromAccountingSettings() {
  if (cachedDefaultCompany !== undefined)
    return cachedDefaultCompany;

  const settings = await GetAccountingSettings({ value: false } as Ref<boolean>).catch(() => null);
  cachedDefaultCompany = normalizeText(settings?.defaultCompany);

  return cachedDefaultCompany;
}

async function resolveSalesInvoiceCompany(...candidates: unknown[]) {
  const explicitCompany = candidates
    .map(normalizeText)
    .find((value): value is string => Boolean(value));

  return explicitCompany ?? await getDefaultCompanyFromAccountingSettings();
}

function toNumber(value: unknown) {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function toBoolean(value: unknown) {
  if (typeof value === "boolean")
    return value;

  return Number(value ?? 0) === 1;
}

function localPostingDate(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function normalizeDateOnly(value: unknown) {
  const normalized = String(value ?? "").trim();
  const dateOnly = normalized.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? "";

  if (!dateOnly)
    return null;

  return dateOnly;
}

function latestDateOnly(...values: unknown[]) {
  const dates = values
    .map(normalizeDateOnly)
    .filter((date): date is string => Boolean(date));

  return dates.sort().at(-1) ?? localPostingDate();
}

function resolveInvoicePostingDates(postingDate?: unknown, dueDate?: unknown) {
  const resolvedPostingDate = normalizeDateOnly(postingDate) || localPostingDate();

  return {
    postingDate: resolvedPostingDate,
    dueDate: latestDateOnly(dueDate, resolvedPostingDate, localPostingDate()),
  };
}

function resolveUpdateStock(value: unknown) {
  if (value === null || value === undefined || value === "")
    return true;

  return toBoolean(value);
}

function mapDocstatus(value: unknown): SalesInvoiceStatus {
  const numeric = Number(value ?? 0);
  if (numeric === 1)
    return "Submitted";
  if (numeric === 2)
    return "Cancelled";

  return "Draft";
}

/**
 * ⚠️ Returns `undefined` for an unrecognised status so the caller can SKIP the clause.
 * The inline ternary this replaces sent every unknown value to `0`, silently listing
 * drafts for a filter the user believed meant something else.
 */
function statusToDocstatus(status: unknown) {
  const normalized = String(status ?? "").trim();
  if (normalized === "Submitted")
    return 1;
  if (normalized === "Cancelled")
    return 2;
  if (normalized === "Draft")
    return 0;

  return undefined;
}

function isDraftDoc(doc: { docstatus?: unknown } | null | undefined) {
  return Number(doc?.docstatus ?? 0) === 0;
}

function resolvePaymentStatus(row: SalesInvoiceRow, documentStatus: SalesInvoiceStatus) {
  const backendStatus = String(row.status ?? "").trim();
  if (backendStatus && !["Draft", "Submitted", "Cancelled"].includes(backendStatus))
    return backendStatus;

  if (documentStatus === "Draft" || documentStatus === "Cancelled")
    return documentStatus;

  const total = toNumber(row.grand_total);
  const outstanding = toNumber(row.outstanding_amount);

  if (total > 0 && outstanding <= 0)
    return "Paid";

  if (total > 0 && outstanding > 0 && outstanding < total)
    return "Partially Paid";

  return "Unpaid";
}

/**
 * What is still collectable on this invoice, as opposed to what the work cost.
 *
 * ⚠️ THE TWO FIGURES ARE NOT INTERCHANGEABLE AND THE DIFFERENCE IS SOMEONE'S MONEY.
 * `grand_total` ignores prepayments; a boarding stay that took a deposit and two
 * instalments carries most of its value in `total_advance` long before anyone reaches
 * the till. Collecting the gross asks the customer to pay twice.
 *
 * Why the status branch: `outstanding_amount` is only meaningful once the document is
 * submitted — a draft has not hit the ledger, so its outstanding reads 0 and would make
 * every unsubmitted invoice look settled. On a draft the subtraction is the only truth
 * available; on a submitted invoice the backend's own figure wins, because it also nets
 * partial Payment Entries this function cannot see.
 *
 * When `total_advance` is absent — an older payload, or the list query that dropped the
 * field — this collapses to `grand_total`, i.e. exactly the behaviour before this existed.
 */
function resolveAmountDue(row: SalesInvoiceRow, documentStatus: SalesInvoiceStatus) {
  if (documentStatus === "Cancelled")
    return 0;

  const grandTotal = toNumber(row.grand_total);
  const netOfAdvances = Math.max(grandTotal - toNumber(row.total_advance), 0);

  if (documentStatus === "Draft")
    return netOfAdvances;

  const outstanding = Number(row.outstanding_amount);

  return Number.isFinite(outstanding) && outstanding > 0 ? outstanding : netOfAdvances;
}

function mapSalesInvoice(row: SalesInvoiceRow): SalesInvoiceListItem {
  const documentStatus = mapDocstatus(row.docstatus);
  const discountAmount = toNumber(row.discount_amount);
  const grandTotal = toNumber(row.grand_total);
  const netTotal = toNumber(row.net_total) || toNumber(row.total) || (grandTotal + discountAmount);

  return {
    id: String(row.name ?? ""),
    customer: row.customer ? String(row.customer) : null,
    customerName: row.customer_name ? String(row.customer_name) : (row.customer ? String(row.customer) : null),
    company: normalizeText(row.company),
    postingDate: row.posting_date ? String(row.posting_date) : null,
    dueDate: row.due_date ? String(row.due_date) : null,
    billNo: row.po_no ? String(row.po_no) : null,
    warehouse: row.set_warehouse ? String(row.set_warehouse) : null,
    remarks: row.remarks ? String(row.remarks) : null,
    netTotal,
    totalTaxesAndCharges: toNumber(row.total_taxes_and_charges),
    currency: normalizeText(row.currency),
    sellingPriceList: normalizeText(row.selling_price_list),
    discountAmount,
    discountPercent: toNumber(row.additional_discount_percentage),
    discountOn: row.apply_discount_on ? String(row.apply_discount_on) : null,
    // Deliberately still the GROSS. The invoice list, the print view and the edit form all
    // want what the work cost, and `netTotal` above is derived from it. The amount to collect
    // is `amountDue` and nothing else should be used for that.
    total: grandTotal,
    outstandingAmount: toNumber(row.outstanding_amount),
    totalAdvance: toNumber(row.total_advance),
    amountDue: resolveAmountDue(row, documentStatus),
    status: documentStatus,
    paymentStatus: resolvePaymentStatus(row, documentStatus),
    updateStock: resolveUpdateStock(row.update_stock),
    isReturn: toBoolean(row.is_return),
    isPos: toBoolean(row.is_pos),
    returnAgainst: row.return_against ? String(row.return_against) : null,
    amendedFrom: row.amended_from ? String(row.amended_from) : null,
    branch: normalizeText(row.branch),
    owner: normalizeText(row.owner),
    creation: normalizeText(row.creation),
  };
}

function mapSalesInvoiceItem(row: SalesInvoiceItemRow, index: number): SalesInvoiceDraftItem {
  const qty = toNumber(row.qty);
  const rate = toNumber(row.rate);
  const source = parseInvoiceItemSource(row.description);

  // A row priced without a line discount comes back with price_list_rate === rate, so
  // `listRate` collapses to `rate` and the row reads exactly as it always did.
  const listRate = toNumber(row.price_list_rate) || rate;
  const discountPercent = toNumber(row.discount_percentage);

  return {
    id: String(row.name ?? `sales-invoice-item-${index}`),
    itemCode: row.item_code ? String(row.item_code) : null,
    itemName: row.item_name ? String(row.item_name) : (row.item_code ? String(row.item_code) : null),
    qty,
    rate,
    amount: toNumber(row.amount) || qty * rate,
    description: row.description ? String(row.description) : null,
    sourceDoctype: source?.sourceDoctype ?? null,
    sourceName: source?.sourceName ?? null,
    listRate,
    discountPercent,
    discountAmount: toNumber(row.discount_amount)
      || (discountPercent > 0 ? roundInvoiceAmount(listRate * (discountPercent / 100)) : 0),
    // ⚠️ Read-side only. These do NOT reach the wire from here — the payload builder emits
    // uom/conversion_factor solely behind an explicit opt-in, because the clinic sync round-trips
    // mapped rows straight back into it.
    uom: normalizeText(row.uom),
    stockUom: normalizeText(row.stock_uom),
    conversionFactor: toNumber(row.conversion_factor) || null,
    warehouse: normalizeText(row.warehouse),
  };
}

function buildInvoiceItemSourceMarker(sourceDoctype?: unknown, sourceName?: unknown) {
  const doctype = normalizeText(sourceDoctype);
  const name = normalizeText(sourceName);

  if (!doctype || !name)
    return null;

  return `[alkokh-source:${doctype}:${name}]`;
}

function parseInvoiceItemSource(description?: unknown) {
  const value = String(description ?? "");
  const match = value.match(/\[alkokh-source:([^:\]]+):([^\]]+)\]/);

  if (!match?.[1] || !match?.[2])
    return null;

  return {
    sourceDoctype: match[1],
    sourceName: match[2],
  };
}

function buildInvoiceItemDescription(item: SalesInvoiceDraftItem) {
  const marker = buildInvoiceItemSourceMarker(item.sourceDoctype, item.sourceName);
  const description = normalizeText(item.description);

  if (!marker)
    return description || undefined;

  if (description?.includes(marker))
    return description;

  return [description, marker].filter(Boolean).join("\n");
}

function mapSalesInvoiceDetail(row: SalesInvoiceRow): SalesInvoiceDetail {
  return {
    ...mapSalesInvoice(row),
    modified: row.modified ? String(row.modified) : null,
    // Free on a full doc read — `SALES_INVOICE_BASE_FIELDS` is untouched, so no list query
    // gains a field it could fail on.
    postingTime: row.posting_time ? String(row.posting_time) : null,
    items: (row.items ?? []).map(mapSalesInvoiceItem),
  };
}

/**
 * Resolves a row's line discount, or `null` when it has none.
 *
 * ⚠️ `null` is the contract that keeps POS and the clinic safe. Every caller outside the
 * invoice edit screen (`buildDraftInvoiceItem`, the manual
 * create page, the replacement half of a return) constructs its rows WITHOUT `listRate`
 * or `discountPercent`, so this returns `null`, `price_list_rate` collapses back to
 * `rate`, and the emitted payload is byte-identical to what it has always been.
 */
function resolveLineDiscount(item: SalesInvoiceDraftItem, qty: number) {
  const percent = Number(item.discountPercent ?? 0);
  const listRate = Number(item.listRate ?? 0);

  if (!(percent > 0) || !(listRate > 0))
    return null;

  const clampedPercent = roundInvoiceAmount(Math.min(percent, 100));
  const perUnitDiscount = roundInvoiceAmount(listRate * (clampedPercent / 100));
  const netRate = roundInvoiceAmount(Math.max(listRate - perUnitDiscount, 0));

  return {
    listRate: roundInvoiceAmount(listRate),
    percent: clampedPercent,
    perUnitDiscount,
    netRate,
    amount: roundInvoiceAmount(netRate * qty),
  };
}

function buildSalesInvoiceItems(
  items: SalesInvoicePayload["items"],
  options?: { emitUom?: boolean },
) {
  return items
    .filter(item => String(item.itemCode ?? "").trim() && Number(item.qty ?? 0) > 0)
    .map((item) => {
      const qty = Number(item.qty ?? 0);
      const discount = resolveLineDiscount(item, qty);
      const rate = discount ? discount.netRate : Number(item.rate ?? 0);
      const listRate = discount ? discount.listRate : rate;
      const amount = discount ? discount.amount : Number(item.amount ?? qty * rate);

      return {
        ...(item.id && !String(item.id).startsWith("new-sales-invoice-item-") && !String(item.id).startsWith("sales-invoice-item-")
          ? { name: item.id }
          : {}),
        doctype: "Sales Invoice Item",
        item_code: item.itemCode,
        description: buildInvoiceItemDescription(item),
        qty,
        rate,
        price_list_rate: listRate,
        base_price_list_rate: listRate,
        base_rate: rate,
        amount,
        base_amount: amount,
        // Spread, not a `0` default: an undiscounted row must not start sending two new
        // keys to the clinic and POS paths that never sent them before.
        ...(discount
          ? {
              discount_percentage: discount.percent,
              discount_amount: discount.perUnitDiscount,
            }
          : {}),
        // ⚠️ Same discipline, and the gate matters more here: without `emitUom` this spread is
        // empty and the emitted object is byte-identical to what the clinic sync has always sent.
        // The two keys travel TOGETHER — a `uom` without its factor lets the server derive one
        // that may disagree with the rate the operator saw.
        ...(options?.emitUom && item.uom && Number(item.conversionFactor) > 0
          ? {
              uom: item.uom,
              conversion_factor: Number(item.conversionFactor),
            }
          : {}),
      };
    });
}

function buildSalesInvoiceReturnItems(items: SalesInvoiceReturnReplacePayload["returnItems"]) {
  return items
    .filter(item => String(item.itemCode ?? "").trim() && Number(item.qty ?? 0) > 0)
    .map((item) => {
      const qty = -Math.abs(Number(item.qty ?? 0));
      const rate = Number(item.rate ?? 0);
      const amount = qty * rate;

      return {
        doctype: "Sales Invoice Item",
        item_code: item.itemCode,
        qty,
        rate,
        price_list_rate: rate,
        base_price_list_rate: rate,
        base_rate: rate,
        amount,
        base_amount: amount,
      };
    });
}

function buildSalesInvoiceDiscountFields(payload: SalesInvoicePayload) {
  // ⚠️ `UpdateSalesInvoice` builds `{ ...existingDoc, ...buildSalesInvoiceDoc(...) }`, so an
  // EMPTY return means "leave the document's own discount alone", not "clear it". Every
  // healthcare/append/sync/return caller omits these keys entirely and must keep that
  // behaviour; only a caller that OWNS the discount — the invoice edit screen — may clear it.
  const mentionsDiscount = payload.discountType !== undefined
    || payload.discountAmount !== undefined
    || payload.discountPercent !== undefined;

  const discountAmount = Number(payload.discountAmount ?? 0);
  const discountPercent = Number(payload.discountPercent ?? 0);

  // ⚠️ Both levers are always written together. Sending only the one in use leaves the other
  // alive via `...existingDoc`; the backend then recomputes from the stale percentage and
  // silently discards the fixed amount the user just typed.
  if (payload.discountType === "Percentage" && discountPercent > 0) {
    return {
      apply_discount_on: "Grand Total",
      additional_discount_percentage: Math.min(discountPercent, 100),
      discount_amount: 0,
    };
  }

  if (discountAmount > 0) {
    return {
      apply_discount_on: "Grand Total",
      additional_discount_percentage: 0,
      discount_amount: discountAmount,
    };
  }

  if (!mentionsDiscount)
    return {};

  return {
    apply_discount_on: "Grand Total",
    additional_discount_percentage: 0,
    discount_amount: 0,
  };
}

function calculateInvoiceItemsTotal(items: SalesInvoicePayload["items"]) {
  return items.reduce((sum, item) => {
    const qty = Number(item.qty ?? 0);
    const rate = Number(item.rate ?? 0);
    const amount = Number(item.amount ?? qty * rate);

    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
}

function calculateInvoicePaymentAmount(payload: SalesInvoicePayload) {
  const subtotalAmount = calculateInvoiceItemsTotal(payload.items);
  const discountAmount = Number(payload.discountAmount ?? 0);
  const discountPercent = Number(payload.discountPercent ?? 0);
  const resolvedDiscountAmount = payload.discountType === "Percentage" && discountPercent > 0
    ? subtotalAmount * (Math.min(discountPercent, 100) / 100)
    : discountAmount;
  const totalAmount = Math.max(subtotalAmount - Math.min(resolvedDiscountAmount, subtotalAmount), 0);
  const paidAmount = Number(payload.paidAmount ?? totalAmount);

  return Math.min(Math.max(paidAmount, 0), totalAmount);
}

function buildSalesInvoicePosFields(payload: SalesInvoicePayload) {
  if (!payload.isPos)
    return {};

  const paymentMode = normalizeText(payload.paymentMode);
  const paymentAmount = calculateInvoicePaymentAmount(payload);
  const cashAccount = normalizeText(payload.cashAccount);
  const useCashAccountForPayment = String(paymentMode ?? "").toLowerCase().includes("cash");

  return {
    is_pos: 1,
    pos_profile: payload.posProfile || undefined,
    custom_pos_profile: payload.posProfile || undefined,
    cash_account: cashAccount || undefined,
    cashier_cash_account: cashAccount || undefined,
    custom_cashier_cash_account: cashAccount || undefined,
    payments: paymentMode && paymentAmount > 0
      ? [
          {
            doctype: "Sales Invoice Payment",
            mode_of_payment: paymentMode,
            amount: paymentAmount,
            base_amount: paymentAmount,
            account: useCashAccountForPayment ? cashAccount || undefined : undefined,
            default: 1,
          },
        ]
      : [],
  };
}

function resolvePositiveRate(value: unknown) {
  const rate = Number(value ?? 0);
  return Number.isFinite(rate) && rate > 0 ? rate : 1;
}

function buildSalesInvoiceCurrencyFields(source: SalesInvoiceCurrencySource) {
  const sellingPriceList = normalizeText(source.sellingPriceList)
    || normalizeText(source.selling_price_list)
    || DEFAULT_SELLING_PRICE_LIST;
  const currency = normalizeText(source.currency) || DEFAULT_SALES_INVOICE_CURRENCY;
  const priceListCurrency = normalizeText(source.priceListCurrency)
    || normalizeText(source.price_list_currency)
    || currency;

  return {
    selling_price_list: sellingPriceList,
    currency,
    price_list_currency: priceListCurrency,
    conversion_rate: resolvePositiveRate(source.conversion_rate),
    plc_conversion_rate: resolvePositiveRate(source.plc_conversion_rate),
  };
}

function buildSalesInvoiceDoc(payload: SalesInvoicePayload, company?: string | null) {
  const invoiceDates = resolveInvoicePostingDates(payload.postingDate, payload.dueDate);
  const resolvedCompany = normalizeText(company) || normalizeText(payload.company);

  return {
    doctype: SALES_INVOICE_DOCTYPE,
    customer: payload.customer,
    company: resolvedCompany || undefined,
    posting_date: invoiceDates.postingDate,
    due_date: invoiceDates.dueDate,
    po_no: payload.billNo || undefined,
    set_warehouse: payload.warehouse || undefined,
    remarks: payload.remarks || undefined,
    update_stock: payload.updateStock ? 1 : 0,
    ignore_pricing_rule: 1,
    ...buildSalesInvoicePosFields(payload),
    ...buildSalesInvoiceCurrencyFields(payload),
    ...buildSalesInvoiceDiscountFields(payload),
    items: buildSalesInvoiceItems(payload.items, { emitUom: payload.emitItemUom === true }),
  };
}

function buildDraftInvoiceItem(payload: SalesInvoiceAppendItemPayload): SalesInvoiceDraftItem {
  const qty = Number(payload.qty ?? 1) || 1;
  const rate = Number(payload.rate ?? 0) || 0;
  const sourceMarker = buildInvoiceItemSourceMarker(payload.sourceDoctype, payload.sourceName);

  return {
    id: `new-sales-invoice-item-${Date.now()}`,
    itemCode: payload.itemCode,
    itemName: payload.itemName || payload.itemCode,
    qty,
    rate,
    amount: qty * rate,
    description: [payload.itemName || payload.itemCode, sourceMarker].filter(Boolean).join("\n") || null,
    sourceDoctype: normalizeText(payload.sourceDoctype),
    sourceName: normalizeText(payload.sourceName),
  };
}

async function fetchSalesInvoiceDoc(id: string, config?: { headers?: Record<string, string> }) {
  const response = await salesInvoiceService.get<any>(
    "frappe.client.get",
    {},
    {
      doctype: SALES_INVOICE_DOCTYPE,
      name: id,
    },
    undefined,
    config,
    FrappeMethodType.method,
  );

  return response?.message ?? response?.data ?? response ?? null;
}

export async function GetSalesInvoices(
  loading: Ref<boolean>,
  filter: SalesInvoiceFilterParam = {},
): Promise<SalesInvoiceListResult | null> {
  return withFrappeLoading(loading, async () => {
    const filterDocstatus = statusToDocstatus(filter.status);

    const filters: FrappeFilterClause[] = [
      ...(filter.filters ?? []),
      ...(filterDocstatus === undefined
        ? []
        : [[SALES_INVOICE_DOCTYPE, "docstatus", "=", filterDocstatus] as FrappeFilterClause]),
      ...(filter.customer
        ? [[SALES_INVOICE_DOCTYPE, "customer", "=", filter.customer] as FrappeFilterClause]
        : []),
      ...(filter.company
        ? [[SALES_INVOICE_DOCTYPE, "company", "=", filter.company] as FrappeFilterClause]
        : []),
      // `is_return` / `is_pos` are Frappe Check fields (int NOT NULL DEFAULT 0), so "= 0"
      // cannot silently drop rows the way it would against a nullable column.
      ...(filter.excludeReturns
        ? [[SALES_INVOICE_DOCTYPE, "is_return", "=", 0] as FrappeFilterClause]
        : []),
      ...(filter.excludePos
        ? [[SALES_INVOICE_DOCTYPE, "is_pos", "=", 0] as FrappeFilterClause]
        : []),
      ...(filter.returnAgainst
        ? [[SALES_INVOICE_DOCTYPE, "return_against", "=", filter.returnAgainst] as FrappeFilterClause]
        : []),
      ...(filter.branch
        ? [[SALES_INVOICE_DOCTYPE, "branch", "=", filter.branch] as FrappeFilterClause]
        : []),
    ];

    const orFilters: FrappeFilterClause[] = filter.search?.trim()
      ? [
          [SALES_INVOICE_DOCTYPE, "name", "like", `%${filter.search.trim()}%`],
          [SALES_INVOICE_DOCTYPE, "customer", "like", `%${filter.search.trim()}%`],
          [SALES_INVOICE_DOCTYPE, "customer_name", "like", `%${filter.search.trim()}%`],
          [SALES_INVOICE_DOCTYPE, "po_no", "like", `%${filter.search.trim()}%`],
          [SALES_INVOICE_DOCTYPE, "remarks", "like", `%${filter.search.trim()}%`],
        ]
      : [];

    orFilters.push(...(filter.orFilters ?? []));

    const serializedFilters = serializeFrappeFilters(filters);
    const serializedOrFilters = serializeFrappeFilters(orFilters);

    async function runQuery() {
      const [response, total] = await Promise.all([
        salesInvoiceService.get<{ data?: SalesInvoiceRow[] }>(
          "",
          {
            limit_start: filter.limit_start ?? 0,
            limit_page_length: filter.limit_page_length ?? 10,
            order_by: filter.order_by ?? "modified desc",
            ...(serializedFilters ? { filters: serializedFilters } : {}),
            ...(serializedOrFilters ? { or_filters: serializedOrFilters } : {}),
          },
          {},
          salesInvoiceFields(),
        ),
        fetchFrappeDocCount(
          salesInvoiceService,
          SALES_INVOICE_DOCTYPE,
          serializedFilters,
          serializedOrFilters,
        ).catch(() => 0),
      ]);

      const normalized = normalizeFrappeListResult(response, total);

      return {
        data: normalized.data.map(mapSalesInvoice),
        total: normalized.total,
      };
    }

    try {
      return await runQuery();
    } catch (error: any) {
      // The one retry. See `advanceFieldUnavailable`: losing the advance figure costs a
      // cashier accuracy, losing the whole list costs them the sale.
      if (!advanceFieldUnavailable && namesAdvanceField(error)) {
        advanceFieldUnavailable = true;

        try {
          return await runQuery();
        } catch (retryError: any) {
          console.error(retryError?.message ?? retryError);
          return null;
        }
      }

      console.error(error?.message ?? error);
      return null;
    }
  });
}

export async function CreateSalesInvoice(
  loading: Ref<boolean>,
  payload: SalesInvoicePayload,
): Promise<SalesInvoiceListItem | null> {
  return withFrappeLoading(loading, async () => {
    const company = await resolveSalesInvoiceCompany(payload.company);
    // Stamp the clinic when the backend can't infer it (opens the picker on first use).
    const doc = await applyBranchToDoc(SALES_INVOICE_DOCTYPE, buildSalesInvoiceDoc(payload, company));
    const response = await salesInvoiceService.post<any>(
      {
        doc: JSON.stringify(doc),
      },
      "frappe.client.insert",
      {},
      undefined,
      FrappeMethodType.method,
    );

    return mapSalesInvoice(response?.message ?? response?.data ?? response ?? {});
  });
}

export async function CreateSalesInvoiceReturnAndReplacement(
  loading: Ref<boolean>,
  payload: SalesInvoiceReturnReplacePayload,
): Promise<SalesInvoiceReturnReplaceResult | null> {
  return withFrappeLoading(loading, async () => {
    const sourceInvoice = await fetchSalesInvoiceDoc(payload.sourceInvoiceId);
    if (!sourceInvoice)
      return null;

    const sourceDetail = mapSalesInvoiceDetail(sourceInvoice);
    if (sourceDetail.status !== "Submitted")
      throw new Error("Only submitted sales invoices can be returned.");
    if (sourceDetail.isReturn)
      throw new Error("Return invoices cannot be returned again.");
    if (!sourceDetail.customer)
      throw new Error("Sales invoice customer is required.");

    const returnItems = buildSalesInvoiceReturnItems(payload.returnItems);
    if (!returnItems.length)
      throw new Error("Add at least one returned item.");

    const postingDate = normalizeDateOnly(payload.postingDate) || localPostingDate();
    const remarks = normalizeText(payload.remarks)
      || `Return against sales invoice ${sourceDetail.id}`;

    const refundAmountLimit = Math.abs(
      returnItems.reduce((sum, item) => sum + Math.abs(Number(item.amount ?? 0)), 0),
    );
    const refundAmount = Math.min(
      Math.max(Number(payload.refundAmount ?? refundAmountLimit), 0),
      refundAmountLimit,
    );
    const shouldRefund = Boolean(payload.refundFromCashier) && refundAmount > 0;
    const updateStock = payload.updateStock ?? sourceDetail.updateStock;

    if (shouldRefund && !normalizeText(payload.posProfile))
      throw new Error("Active cashier is required to refund money.");
    if (shouldRefund && !normalizeText(payload.cashAccount))
      throw new Error("Active cashier cash account is required to refund money.");

    // A return is a fresh Sales Invoice insert, so it hits the branch hook too.
    // Inherit the source invoice's clinic when it has one; only fall back to the
    // picker (via applyBranchToDoc) if the source somehow carries none.
    const returnDoc = await applyBranchToDoc(SALES_INVOICE_DOCTYPE, {
      doctype: SALES_INVOICE_DOCTYPE,
      customer: sourceDetail.customer,
      company: sourceInvoice.company || undefined,
      posting_date: postingDate,
      due_date: postingDate,
      is_return: 1,
      return_against: sourceDetail.id,
      update_stock: updateStock ? 1 : 0,
      remarks,
      ignore_pricing_rule: 1,
      branch: sourceInvoice.branch || undefined,
      ...buildSalesInvoiceCurrencyFields(sourceInvoice),
      items: returnItems,
    });
    const returnResponse = await salesInvoiceService.post<any>(
      {
        doc: JSON.stringify(returnDoc),
      },
      "frappe.client.insert",
      {},
      undefined,
      FrappeMethodType.method,
    );

    let createdReturnInvoice = mapSalesInvoice(
      returnResponse?.message ?? returnResponse?.data ?? returnResponse ?? {},
    );

    if (!createdReturnInvoice)
      return null;

    const submittedReturnInvoice = shouldRefund && createdReturnInvoice.id
      ? await SubmitSalesInvoice({ value: false } as Ref<boolean>, createdReturnInvoice.id)
      : null;

    if (submittedReturnInvoice)
      createdReturnInvoice = submittedReturnInvoice;

    const refundPaymentEntry = shouldRefund && createdReturnInvoice.id
      ? await CreateSalesInvoicePaymentEntry({ value: false } as Ref<boolean>, {
          salesInvoiceId: createdReturnInvoice.id,
          paymentType: "Pay",
          postingDate,
          amount: refundAmount,
          posProfile: payload.posProfile || undefined,
          cashAccount: payload.cashAccount || undefined,
          modeOfPayment: payload.modeOfPayment || undefined,
          remarks: `Refund against return invoice ${createdReturnInvoice.id}`,
        })
      : null;

    const replacementItems = payload.replacementItems
      ?.filter(item => String(item.itemCode ?? "").trim() && Number(item.qty ?? 0) > 0)
      .map(item => ({
        id: `replacement-sales-invoice-item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        itemCode: item.itemCode,
        itemName: item.itemName ?? item.itemCode,
        qty: Number(item.qty ?? 0),
        rate: Number(item.rate ?? 0),
        amount: Number(item.qty ?? 0) * Number(item.rate ?? 0),
      })) ?? [];

    const replacementInvoice = replacementItems.length
      ? await CreateSalesInvoice({ value: false } as Ref<boolean>, {
          customer: sourceDetail.customer || "",
          company: sourceDetail.company || sourceInvoice.company || null,
          postingDate,
          dueDate: postingDate,
          billNo: null,
          remarks: `Replacement for sales invoice ${sourceDetail.id}`,
          updateStock: payload.updateStock ?? sourceDetail.updateStock,
          items: replacementItems,
        })
      : null;

    return {
      returnInvoice: createdReturnInvoice,
      replacementInvoice,
      refundPaymentEntry,
    };
  });
}

/**
 * Read one invoice, and let the failure through.
 *
 * ⚠️ THE SIBLING BELOW SWALLOWS THE CAUSE, AND FOR SOME CALLERS THAT IS WRONG.
 * `GetSalesInvoiceDetail` collapses every failure to `null`, so a caller cannot
 * tell "no such invoice" from "you may not read this one" — which is exactly the
 * distinction POS needs before it offers to settle a document at a till. This
 * version throws, so the `ApiError` (with its `status`) survives.
 *
 * `suppressAuthorizationBanner` opts the request out of the page-level warning
 * the 403 branch otherwise raises (`httpClient.ts`). Pass it only when the caller
 * reports the refusal itself — two messages for one failure is worse than one.
 */
export async function ReadSalesInvoiceDetail(
  loading: Ref<boolean>,
  salesInvoiceId: string,
  options?: { suppressAuthorizationBanner?: boolean },
): Promise<SalesInvoiceDetail> {
  return withFrappeLoading(loading, async () => {
    const doc = await fetchSalesInvoiceDoc(
      salesInvoiceId,
      options?.suppressAuthorizationBanner
        ? { headers: { "X-Suppress-Authorization-Banner": "1" } }
        : undefined,
    );

    if (!doc)
      throw new Error(`Invoice ${salesInvoiceId} could not be read.`);

    return mapSalesInvoiceDetail(doc);
  });
}

export async function GetSalesInvoiceDetail(
  loading: Ref<boolean>,
  salesInvoiceId: string,
): Promise<SalesInvoiceDetail | null> {
  try {
    return await ReadSalesInvoiceDetail(loading, salesInvoiceId);
  } catch (error: any) {
    console.error(error?.message ?? error);

    return null;
  }
}

export async function UpdateSalesInvoice(
  loading: Ref<boolean>,
  salesInvoiceId: string,
  payload: SalesInvoicePayload,
): Promise<SalesInvoiceDetail | null> {
  return withFrappeLoading(loading, async () => {
    const existingDoc = await fetchSalesInvoiceDoc(salesInvoiceId);
    if (!existingDoc)
      return null;
    if (!isDraftDoc(existingDoc))
      throw new Error("Only draft sales invoices can be edited.");

    const company = await resolveSalesInvoiceCompany(payload.company, existingDoc.company);
    const doc = {
      ...existingDoc,
      name: salesInvoiceId,
      ...buildSalesInvoiceDoc(payload, company),
      set_posting_time: 1,
    };

    const response = await salesInvoiceService.post<any>(
      {
        doc: JSON.stringify(doc),
      },
      "frappe.client.save",
      {},
      undefined,
      FrappeMethodType.method,
    );

    const savedDoc = response?.message ?? response?.data ?? response ?? {};

    return mapSalesInvoiceDetail(savedDoc);
  });
}

export async function SubmitSalesInvoice(
  loading: Ref<boolean>,
  salesInvoiceId: string,
): Promise<SalesInvoiceDetail | null> {
  return withFrappeLoading(loading, async () => {
    const existingDoc = await fetchSalesInvoiceDoc(salesInvoiceId);
    if (!existingDoc)
      return null;
    if (!isDraftDoc(existingDoc))
      throw new Error("Only draft sales invoices can be submitted.");

    const invoiceDates = resolveInvoicePostingDates(existingDoc.posting_date, existingDoc.due_date);
    const company = await resolveSalesInvoiceCompany(existingDoc.company);
    const doc = {
      ...existingDoc,
      company: company || existingDoc.company || undefined,
      posting_date: invoiceDates.postingDate,
      due_date: invoiceDates.dueDate,
      ignore_pricing_rule: 1,
      ...buildSalesInvoiceCurrencyFields(existingDoc),
    };

    const response = await salesInvoiceService.post<any>(
      {
        doc: JSON.stringify(doc),
      },
      "frappe.client.submit",
      {},
      undefined,
      FrappeMethodType.method,
    );

    const submittedDoc = response?.message ?? response?.data ?? response ?? {};

    return mapSalesInvoiceDetail(submittedDoc);
  });
}

/**
 * The ONE place that decides which Draft a new charge may be appended to. All three
 * append/ensure entry points below go through it so the rule cannot drift between them.
 *
 * Scope — customer + docstatus 0 was never enough:
 *  - `company`: resolved from Accounting Settings. Skipped when unknown rather than
 *    guessed, because a wrong company filter silently hides the real draft and produces
 *    the duplicate it was added to prevent.
 *  - `is_return = 0`: a credit note also sits at docstatus 0. Appending today's
 *    consultation to a refund would net it off against the refund.
 *  - `is_pos = 0`: a POS sale is a closed, paid, stock-moving document; it is only at
 *    docstatus 0 for the instant between insert and submit, and appending into that
 *    window would both corrupt the sale and race its submit.
 *
 * FAILURE IS NOT EMPTINESS. `GetSalesInvoices` resolves to `null` when the read FAILED
 * and to `{ data: [] }` when the customer genuinely has no draft. Collapsing those is
 * exactly how a duplicate invoice gets created: a search that could not run reports
 * "no draft exists" and the caller cheerfully opens a second one. A failed read throws.
 */
async function findOpenDraftSalesInvoice(
  loading: Ref<boolean>,
  customer: string,
): Promise<SalesInvoiceListItem | null> {
  const company = await resolveSalesInvoiceCompany();
  const existing = await GetSalesInvoices(loading, {
    customer,
    status: "Draft",
    company,
    excludeReturns: true,
    excludePos: true,
    limit_start: 0,
    limit_page_length: 1,
    order_by: "modified desc",
  });

  if (!existing)
    throw new Error("Could not check whether this customer already has an open draft invoice.");

  return existing.data[0] ?? null;
}

export async function AppendItemToOpenSalesInvoice(
  loading: Ref<boolean>,
  payload: SalesInvoiceAppendItemPayload,
): Promise<SalesInvoiceAppendItemResult | null> {
  return withFrappeLoading(loading, async () => {
    const customer = String(payload.customer ?? "").trim();
    const itemCode = String(payload.itemCode ?? "").trim();

    if (!customer || !itemCode)
      return null;

    const item = buildDraftInvoiceItem({
      ...payload,
      customer,
      itemCode,
    });
    const openInvoice = await findOpenDraftSalesInvoice(loading, customer);

    if (openInvoice?.id) {
      const detail = await GetSalesInvoiceDetail(loading, openInvoice.id);
      if (!detail)
        return null;
      const sourceMarker = buildInvoiceItemSourceMarker(payload.sourceDoctype, payload.sourceName);
      const existingSourceIndex = sourceMarker
        ? detail.items.findIndex(item => String(item.description ?? "").includes(sourceMarker))
        : -1;
      const nextItems = existingSourceIndex > -1
        ? detail.items.map((existingItem, index) =>
            index === existingSourceIndex
              ? {
                  ...item,
                  id: existingItem.id,
                  description: item.description,
                }
              : existingItem,
          )
        : [
            ...(detail.items || []),
            item,
          ];

      const updated = await UpdateSalesInvoice(loading, detail.id, {
        customer,
        postingDate: detail.postingDate || payload.postingDate || localPostingDate(),
        dueDate: detail.dueDate || payload.dueDate || null,
        billNo: detail.billNo || null,
        remarks: detail.remarks || payload.remarks || null,
        updateStock: detail.updateStock,
        items: nextItems,
      });

      return updated
        ? {
            invoiceId: updated.id,
            created: false,
            itemCount: updated.items.length,
          }
        : null;
    }

    const created = await CreateSalesInvoice(loading, {
      customer,
      postingDate: payload.postingDate || localPostingDate(),
      dueDate: payload.dueDate || null,
      billNo: null,
      remarks: payload.remarks || null,
      updateStock: false,
      items: [item],
    });

    return created
      ? {
          invoiceId: created.id,
          created: true,
          itemCount: 1,
        }
      : null;
  });
}

export async function EnsureOpenDraftSalesInvoice(
  loading: Ref<boolean>,
  customer: string,
): Promise<SalesInvoiceEnsureDraftResult | null> {
  return withFrappeLoading(loading, async () => {
    const normalizedCustomer = String(customer ?? "").trim();
    if (!normalizedCustomer)
      return null;

    const openInvoice = await findOpenDraftSalesInvoice(loading, normalizedCustomer);

    if (openInvoice?.id) {
      return {
        invoiceId: openInvoice.id,
        created: false,
        itemCount: 0,
      };
    }

    // No try/catch. This used to swallow every failure into `null` behind a console.warn
    // that told itself it was fine ("It will be created when billable items are synced"),
    // which is how a broken billing path stayed invisible for 705 drafts. Whoever calls
    // this decides whether a billing failure should sink the operation it rode in on —
    // that decision does not belong here. `null` is returned ONLY for the two genuine
    // "nothing to do" cases: a blank customer, and an insert that came back without an id.
    const created = await CreateSalesInvoice(loading, {
      customer: normalizedCustomer,
      postingDate: localPostingDate(),
      dueDate: null,
      billNo: null,
      remarks: "Draft healthcare billing invoice",
      updateStock: false,
      items: [],
    });

    return created?.id
      ? {
          invoiceId: created.id,
          created: true,
          itemCount: 0,
        }
      : null;
  });
}

/**
 * Cancels a submitted invoice.
 *
 * Guard is deliberately thin — docstatus only. Submitted payments, later stock movements,
 * negative stock and a POS closing entry are all the backend's refusals to make, and they
 * arrive as a readable message through the shared interceptor. Inventing client-side rules
 * here would block cases the backend allows and still miss the ones it does not.
 */
export async function CancelSalesInvoice(
  loading: Ref<boolean>,
  salesInvoiceId: string,
): Promise<SalesInvoiceDetail | null> {
  return withFrappeLoading(loading, async () => {
    const existingDoc = await fetchSalesInvoiceDoc(salesInvoiceId);
    if (!existingDoc)
      return null;

    if (Number(existingDoc.docstatus ?? 0) !== 1)
      throw new Error("Only submitted sales invoices can be cancelled.");

    // ⚠️ frappe.client.cancel takes doctype + name, NOT a { doc } payload like
    // frappe.client.submit does. Sending { doc } here silently cancels nothing.
    const response = await salesInvoiceService.post<any>(
      {
        doctype: SALES_INVOICE_DOCTYPE,
        name: salesInvoiceId,
      },
      "frappe.client.cancel",
      {},
      undefined,
      FrappeMethodType.method,
    );

    const cancelledDoc = response?.message ?? response?.data ?? response ?? {};

    // Re-read rather than trust the returned dict: the response shape is not guaranteed to
    // carry the child table, and a detail without items would blank the page.
    if (Array.isArray(cancelledDoc?.items))
      return mapSalesInvoiceDetail(cancelledDoc);

    const reread = await fetchSalesInvoiceDoc(salesInvoiceId);

    return reread ? mapSalesInvoiceDetail(reread) : null;
  });
}

/** Deletes a draft outright. Resource DELETE is the house pattern for removing a document. */
export async function DeleteSalesInvoiceDraft(
  loading: Ref<boolean>,
  salesInvoiceId: string,
): Promise<SalesInvoiceDeleteResult | null> {
  return withFrappeLoading(loading, async () => {
    const existingDoc = await fetchSalesInvoiceDoc(salesInvoiceId);
    if (!existingDoc)
      return null;

    if (!isDraftDoc(existingDoc))
      throw new Error("Only draft sales invoices can be deleted.");

    await salesInvoiceService.delete(salesInvoiceId);

    return { invoiceId: salesInvoiceId, deleted: true as const };
  });
}

const AMEND_STRIPPED_DOC_KEYS = [
  "name",
  "owner",
  "creation",
  "modified",
  "modified_by",
  "idx",
  "outstanding_amount",
  "paid_amount",
  "base_paid_amount",
  "change_amount",
  "base_change_amount",
];

const AMEND_STRIPPED_ROW_KEYS = [
  "name",
  "parent",
  "parentfield",
  "parenttype",
  "owner",
  "creation",
  "modified",
  "modified_by",
  "docstatus",
  // ⚠️ A Serial and Batch Bundle is a SUBMITTED document owned by the cancelled invoice.
  // Carrying its name into a new draft links the draft to a bundle it does not own.
  "serial_and_batch_bundle",
];

function stripAmendedChildRow(row: Record<string, unknown>) {
  const next: Record<string, unknown> = { ...row };

  for (const key of AMEND_STRIPPED_ROW_KEYS)
    delete next[key];

  return next;
}

/**
 * ⚠️ Clones the RAW document rather than rebuilding through `buildSalesInvoiceDoc`.
 *
 * The mapped `SalesInvoiceDraftItem` shape carries no per-row warehouse, uom,
 * conversion_factor, batch_no or serial_no, so rebuilding would hand back an amended draft
 * whose batched medication lines had lost their batch — exactly the columns `update_stock`
 * needs on submit.
 */
function buildAmendedSalesInvoiceDoc(cancelledDoc: Record<string, any>, salesInvoiceId: string) {
  const doc: Record<string, any> = { ...cancelledDoc };

  for (const key of AMEND_STRIPPED_DOC_KEYS)
    delete doc[key];

  for (const [key, value] of Object.entries(doc)) {
    if (!Array.isArray(value))
      continue;

    doc[key] = value.map(row =>
      row && typeof row === "object" ? stripAmendedChildRow(row as Record<string, unknown>) : row,
    );
  }

  doc.doctype = SALES_INVOICE_DOCTYPE;
  doc.docstatus = 0;
  doc.status = "Draft";
  doc.amended_from = salesInvoiceId;
  doc.ignore_pricing_rule = 1;

  return doc;
}

/**
 * Creates a fresh editable draft from a cancelled invoice.
 *
 * ⚠️ This is an INSERT, and the backend guard currently refuses direct Sales Invoice
 * inserts. Expect a refusal until that guard grows an `amended_from` exemption or a
 * dedicated method; the refusal is surfaced to the user rather than swallowed.
 */
export async function AmendSalesInvoice(
  loading: Ref<boolean>,
  salesInvoiceId: string,
): Promise<SalesInvoiceAmendResult | null> {
  return withFrappeLoading(loading, async () => {
    const cancelledDoc = await fetchSalesInvoiceDoc(salesInvoiceId);
    if (!cancelledDoc)
      return null;

    if (Number(cancelledDoc.docstatus ?? 0) !== 2)
      throw new Error("Only cancelled sales invoices can be amended.");

    // A till sale carries a settled payments[] table. Amending it would produce a draft
    // whose submit re-books cash into the drawer for money never collected a second time.
    if (toBoolean(cancelledDoc.is_pos))
      throw new Error("POS sales cannot be amended.");

    const doc = await applyBranchToDoc(
      SALES_INVOICE_DOCTYPE,
      buildAmendedSalesInvoiceDoc(cancelledDoc, salesInvoiceId),
    );

    const response = await salesInvoiceService.post<any>(
      {
        doc: JSON.stringify(doc),
      },
      "frappe.client.insert",
      {},
      undefined,
      FrappeMethodType.method,
    );

    const created = mapSalesInvoice(response?.message ?? response?.data ?? response ?? {});
    if (!created.id)
      return null;

    return { amendedFrom: salesInvoiceId, invoice: created };
  });
}

/** Resolving each exact split costs one request, so the detail lookup is bounded. */
const PAYMENT_ALLOCATION_DETAIL_LIMIT = 20;

interface PaymentHistorySource {
  id: string;
  postingDate: string | null;
  paymentType: string | null;
  modeOfPayment: string | null;
  referenceNo: string | null;
  paidAmount: number;
  receivedAmount: number;
  status: string;
  party: string | null;
}

function mapPaymentHistoryItem(
  entry: PaymentHistorySource,
  allocatedAmount: number,
  allocationExact: boolean,
): SalesInvoicePaymentHistoryItem {
  const amount = Number(entry.receivedAmount ?? 0) || Number(entry.paidAmount ?? 0);

  return {
    id: entry.id,
    postingDate: entry.postingDate,
    paymentType: entry.paymentType,
    modeOfPayment: entry.modeOfPayment,
    referenceNo: entry.referenceNo,
    amount,
    allocatedAmount,
    allocationExact,
    status: entry.status as SalesInvoiceStatus,
    party: entry.party,
  };
}

/**
 * Payments recorded against one invoice.
 *
 * ⚠️ Returns `null` on a FAILED read and `[]` when there genuinely are none. The panel must
 * keep them apart — "no payments recorded" on an invoice that has them is the dangerous
 * direction.
 *
 * ⚠️ Cancelled payments are deliberately NOT filtered out. Hiding them would make the
 * history lie about a reversal.
 */
export async function GetSalesInvoicePayments(
  loading: Ref<boolean>,
  salesInvoiceId: string,
): Promise<SalesInvoicePaymentHistoryItem[] | null> {
  return withFrappeLoading(loading, async () => {
    if (!String(salesInvoiceId ?? "").trim())
      return [];

    const result = await GetPaymentEntries({ value: false } as Ref<boolean>, {
      filters: [
        // reference_doctype is pinned too, so a Purchase Invoice sharing the name string
        // cannot bleed into a sales invoice's history.
        ["Payment Entry Reference", "reference_doctype", "=", SALES_INVOICE_DOCTYPE],
        ["Payment Entry Reference", "reference_name", "=", salesInvoiceId],
      ] as FrappeFilterClause[],
      limit_start: 0,
      limit_page_length: 50,
      order_by: "posting_date desc",
    });

    if (!result)
      return null;

    // ⚠️ `result.total` counts over the same child-table join, which can multiply rows.
    // The returned array is the honest length.
    const entries = (result.data ?? []) as unknown as PaymentHistorySource[];

    return Promise.all(entries.map(async (entry, index) => {
      const fallbackAmount = Number(entry.receivedAmount ?? 0) || Number(entry.paidAmount ?? 0);

      // The list query does not return the references child table, so the amount allocated
      // to THIS invoice is unknown from it — one payment can settle several invoices.
      if (index >= PAYMENT_ALLOCATION_DETAIL_LIMIT)
        return mapPaymentHistoryItem(entry, fallbackAmount, false);

      try {
        const detail = await GetPaymentEntryDetail({ value: false } as Ref<boolean>, entry.id);
        const reference = detail?.references?.find(item => item.referenceName === salesInvoiceId);

        if (!reference)
          return mapPaymentHistoryItem(entry, fallbackAmount, false);

        return mapPaymentHistoryItem(entry, Number(reference.allocatedAmount ?? 0), true);
      } catch {
        return mapPaymentHistoryItem(entry, fallbackAmount, false);
      }
    }));
  });
}

/**
 * The facts the cancel dialog states before it lets anyone press the button.
 *
 * Lives here rather than in the dialog so the list row action (which has no detail loaded)
 * and the detail page show the user the same thing.
 */
export async function GetSalesInvoiceCancelContext(
  loading: Ref<boolean>,
  salesInvoiceId: string,
): Promise<SalesInvoiceCancelContext | null> {
  return withFrappeLoading(loading, async () => {
    const detail = await GetSalesInvoiceDetail({ value: false } as Ref<boolean>, salesInvoiceId);
    if (!detail)
      return null;

    const [payments, creditNoteResult] = await Promise.all([
      GetSalesInvoicePayments({ value: false } as Ref<boolean>, salesInvoiceId).catch(() => null),
      GetSalesInvoices({ value: false } as Ref<boolean>, {
        returnAgainst: salesInvoiceId,
        limit_page_length: 20,
      }).catch(() => null),
    ]);

    const total = Number(detail.total ?? 0);
    const outstandingAmount = Number(detail.outstandingAmount ?? 0);

    return {
      invoiceId: detail.id,
      total,
      paidAmount: Math.max(total - outstandingAmount, 0),
      outstandingAmount,
      updateStock: detail.updateStock,
      isPos: detail.isPos,
      submittedPayments: (payments ?? []).filter(payment => payment.status === "Submitted"),
      creditNotes: creditNoteResult?.data ?? [],
      // A failed linked-document read must never render as "nothing is linked".
      incomplete: payments === null || creditNoteResult === null,
    };
  });
}

/*
 * ⚠️ `getSalesInvoicePrintUrl` / `getSalesInvoiceDownloadUrl` ARE GONE, AND MUST
 * NOT COME BACK.
 *
 * They pointed at the backend's `/printview` and
 * `frappe.utils.print_format.download_pdf`. The invoice document is now built
 * entirely in the browser — see `src/services/salesInvoiceDocument.ts` and
 * `useSalesInvoiceDocument` — because the layout, the paper size and the
 * language belong to this app, not to a server print format. Both builders also
 * hardcoded `format: "Standard"` and omitted the language, so an Arabic invoice
 * printed in the backend's default language.
 */
